// src/pages/transferReceiveRecord/TransferReceiveRecordThunk.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { postData } from "../../apiservice/apis";
import _ from "lodash";

export const fetchTransferReceiveRecord = createAsyncThunk(
  "transferReceiveRecord/fetch",
  async ({ selectedFilterType, selectedRecordType, filters }, { getState, rejectWithValue }) => {

    const branches = getState().lookups?.branches || [];
    const selectedBranch = (id)=> branches.find((branch => branch.id === id ));

    try {
      // Choose endpoint
      let url = "/inventory/get-transfer-receive-record";
      if (selectedRecordType === "with item") {
        url = "/inventory/get-transfer-receive-record-with-details";
      }

      // If filterType is one of the Item/Group/Category modes, use the details endpoint
      const filterMode = selectedFilterType?.toString?.();
      if (["By Item", "By Group", "By Category"].includes(filterMode)) {
        url = "/inventory/get-transfer-receive-details";
      }

      const payload = {
        selectedFilterType: selectedFilterType || null,
        fromBranchId: filters?.branchId || null,
        userId: filters?.userId || null,
        itemId: filters?.itemId || null,
        groupId: filters?.groupId || null,
        categoryId: filters?.categoryId || null,
        fromDate: filters?.fromDate || null,
        toDate: filters?.toDate || null,
      };

      const res = await postData(url, payload);

      // Map into shapes that UI expects
      // Cases:
      // 1) Summary (without item): list of masters
      // 2) with item (master -> details): flatten to per-item rows
      // 3) By Item / By Group / By Category: backend returns details list (each row is a detail)
      let mapped = [];

      if (["By Item", "By Group", "By Category"].includes(filterMode)) {
        // res.data is an array of detail rows; keep them with consistent fields
        mapped = res.data.map((d, idx) => ({
          id: `${d.t_id}-${d.item_id}-${idx}`,
          t_id: d.t_id,
          t_voucher_no: d.t_voucher_no || d.tran_order_no || d.transfer_no,
          created_date: d.created_date,
          from_branch_name: d.from_branch_name || d.to_branch_name || d.from_branch || "",
          to_branch_name:  selectedBranch(d.to_branch_id)?.name || "",
          item_id: d.item_id,
          item_name: d.item_name,
          item_rate: d.item_rate,
          item_total: d.item_total,
          item_qty: d.item_qty,
          unit_name: d.unit_name || d.unit_symbol || "",
          unit_symbol: d.unit_symbol || d.unit_name || "",
          retail_qty: d.retail_qty || 0,
          serials: Array.isArray(d.serials) ? d.serials : (d.serials ? d.serials.split(",").filter(Boolean).map(s => ({ serial_number: s })) : []),
          is_serial: d.is_serial,
          to_warehouse_name: d.to_warehouse_name,
        }));
        return mapped;
      }

      if (selectedRecordType === "with item") {
        mapped = res.data.flatMap((transfer) =>
          transfer.details.map((item, idx) => ({
            id: `${transfer.t_id}-${item.item_id}-${idx}`,
            t_id: transfer.t_id,
            vch_no: transfer.t_voucher_no,
            date: transfer.created_date,
            fromLocation: transfer.from_location || transfer.from_branch_name || "",
            toLocation: transfer.to_location ||  selectedBranch(transfer.to_branch_id)?.name || "",
            createdBy: transfer.full_name,
            item: item.item_name,
            rate: Number(item.item_rate || 0).toFixed(2),
            qty: `${item.item_qty} ${item.unit_name || ""}`,
            total: Number(item.item_total || 0).toFixed(2),
            masterQty: Number(item.item_qty || 0),
            baseQty: Number(item.retail_qty || 0),
            unitDetails: `Total: Master U. Qty = ${item.item_qty || 0}, Base U. Qty = ${item.retail_qty || 0}`,
            is_serial: item.is_serial,
            serials: item.serials ? (Array.isArray(item.serials) ? item.serials : item.serials.split(",").filter(Boolean).map(s => ({ serial_number: s }))) : [],
            to_warehouse_name: item.to_warehouse_name,
          }))
        );
      } else {
        mapped = res.data.map((row) => ({
          id: row.t_id,
          t_id: row.t_id,
          vch_no: row.t_voucher_no,
          date: row.created_date,
          fromLocation: row.from_location || row.from_branch_name || "",
          toLocation: row.to_location || selectedBranch(row.to_branch_id)?.name || "",
          createdBy: row.full_name,
          total: Number(row.total_amount || 0).toFixed(2),
          transport_cost: Number(row.total_transport_cost || row.transport_cost || 0).toFixed(2),
          status: row.status === "p" ? "Pending" : "Active",
        }));
      }

      return mapped;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
